import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Import the Sanity client and urlFor
import { PortableText } from '@portabletext/react'; // Import PortableText

// Replicated Loading Spinner Component (Consider moving to shared components)
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div>
    <p className="ml-4 text-lg text-gray-700">Loading...</p>
  </div>
);

// Replicated Error Message Component (Consider moving to shared components)
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again later.'}</p>
  </div>
);


const SocialStudies = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('welcome');

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        // Query updated to fetch tabs structure and newsletter data
        const query = `*[_type == "socialStudiesPage" && _id == "socialStudiesPageContent"][0] {
          bannerTitle,
          bannerImage { 
            asset->{
              _id,
              url
            }
          },
          teacherName,
          tabs[]{
            _key,
            tabTitle,
            tabIdentifier,
            contentSections[]{
              _key,
              title,
              body
            }
          },
          newsletterTitle,
          newsletterSubtitle,
          newsletterTeacherName,
          newsletterContent
        }`;
        const data = await client.fetch(query);
        if (data) {
          setPageData(data);
        } else {
          setError('Social Studies Page content not found.');
        }
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch Social Studies page data:", err);
        setError(err.message || 'Failed to load page content. Please try again later.');
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!pageData) {
    // Use ErrorMessage for consistency if no data is found after loading
    return <ErrorMessage message="No content available for this page." />;
  }


  return (
    <div className="bg-white"> {/* Match outer div class */} 
      {/* Banner - Styled like PhysicalEducation */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {pageData.bannerImage?.asset?.url ? (
            <img 
              src={urlFor(pageData.bannerImage).url()} 
              alt={pageData.bannerTitle || 'PS52 Social Studies Program'} 
              className="absolute inset-0 w-full h-full object-cover object-center" 
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 w-full h-full bg-gray-300"></div> // Placeholder background
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle || 'Social Studies'}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-6xl mx-auto">
            
            {/* Tabs */}
            <div className="flex overflow-x-auto pb-2 mb-8">
              {pageData.tabs && pageData.tabs.length > 0 && (
                <button
                  className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                    activeTab === 'welcome'
                      ? 'bg-[#691e31] text-white shadow-md'
                      : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                  }`}
                  onClick={() => setActiveTab('welcome')}
                >
                  {pageData.tabs[0].tabTitle}
                </button>
              )}
              <button
                className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                  activeTab === 'newsletter'
                    ? 'bg-[#691e31] text-white shadow-md'
                    : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                }`}
                onClick={() => setActiveTab('newsletter')}
              >
                {pageData.newsletterTitle || 'Monthly Newsletter'}
              </button>
            </div>

            {/* Welcome Tab Content */}
            {activeTab === 'welcome' && (
              <div className="space-y-6">
                {pageData.tabs && pageData.tabs.length > 0 && pageData.tabs[0].contentSections ? (
                  pageData.tabs[0].contentSections.map((section) => (
                    <section key={section._key} className="bg-[#2a4865]/5 p-5 rounded-lg border-l-4 border-[#2a4865]">
                      <h3 className="text-xl font-bold text-[#2a4865] mb-3 relative after:content-[''] after:absolute after:w-10 after:h-0.5 after:bg-yellow-500 after:-bottom-1 after:left-0">
                        {section.title}
                      </h3>
                      <div className="prose prose-sm sm:prose max-w-none text-gray-700 prose-headings:text-[#691e31] prose-h4:text-lg prose-h4:font-medium prose-strong:font-semibold prose-ul:list-disc prose-ul:ml-5 prose-li:mb-1">
                        {section.body && <PortableText value={section.body} />} 
                      </div>
                    </section>
                  ))
                ) : (
                  <p className="text-center text-gray-500">No content sections available.</p>
                )}
              </div>
            )}

            {/* Newsletter Tab Content */}
            {activeTab === 'newsletter' && (
              <div className="space-y-6">
                <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="bg-[#2a4865] text-white p-6 rounded-t-lg">
                    <h3 className="text-2xl font-bold mb-2 text-white">
                      {pageData.newsletterSubtitle || 'January 2025 Newsletter'}
                    </h3>
                    <p className="text-white/80 text-sm">
                      Social Studies • {pageData.newsletterTeacherName || 'Mr. Johnson'}
                    </p>
                  </div>
                  
                  <div className="p-6 prose prose-lg max-w-none">
                    {pageData.newsletterContent ? (
                      <PortableText value={pageData.newsletterContent} />
                    ) : (
                      <div>
                        <h2 className="text-xl font-bold text-[#691e31] mb-4">Exploring Our World Together! 🌍</h2>
                        
                        <p className="text-gray-700 mb-6">
                          Happy New Year! We're excited to continue our journey through history, geography, and cultures. 
                          This month we're learning about how communities change over time and understanding different perspectives.
                        </p>

                        <h2 className="text-xl font-bold text-[#691e31] mb-4">This Month's Explorations 🗺️</h2>
                        
                        <p className="text-gray-700 mb-3">
                          <strong>American History:</strong> We're studying the civil rights movement and learning about 
                          important figures who fought for equality and justice in our country.
                        </p>
                        
                        <p className="text-gray-700 mb-3">
                          <strong>Geography Skills:</strong> Students are practicing map reading, understanding different types of maps, 
                          and learning about climate and natural resources around the world.
                        </p>
                        
                        <p className="text-gray-700 mb-6">
                          <strong>Cultural Awareness:</strong> We're exploring different traditions, celebrations, and ways of life 
                          to help students understand and appreciate diversity.
                        </p>

                        <h2 className="text-xl font-bold text-[#691e31] mb-4">Important Dates 📅</h2>
                        
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                          <li><strong>January 15:</strong> MLK Day - No School (Perfect time to discuss civil rights)</li>
                          <li><strong>January 20:</strong> Presidential Inauguration Discussion</li>
                          <li><strong>January 27:</strong> Geography Bee Competition</li>
                          <li><strong>February 3:</strong> Black History Month Kickoff</li>
                        </ul>

                        <h2 className="text-xl font-bold text-[#691e31] mb-4">Connecting Learning to Home 🏠</h2>
                        
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                          <li>Discuss current events age-appropriately with your child</li>
                          <li>Explore your family's history and cultural traditions</li>
                          <li>Use maps when planning trips or talking about places in the news</li>
                          <li>Visit local museums, historical sites, or cultural centers</li>
                          <li>Read biographies of historical figures together</li>
                        </ul>

                        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                          <h3 className="font-bold text-[#691e31] mb-2">📧 Building Global Citizens!</h3>
                          <p className="text-gray-700 text-sm">
                            Social studies helps students understand their place in the world and develop empathy for others. 
                            I'd love to hear about your family's cultural traditions or historical connections!
                          </p>
                          <p className="text-gray-700 text-sm mt-2">
                            <strong>Let's explore the world together!</strong><br/>
                            <em>- {pageData.newsletterTeacherName || 'Mr. Johnson'}, Social Studies Teacher</em>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SocialStudies; 